import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Classroom } from "@/types/classroom";
import StudentForm from "./classroom/StudentForm";
import StudentsTable from "./classroom/StudentsTable";
import CreateClassroomDialog from "./classroom/CreateClassroomDialog";
import {
  useGetClasses,
  useGetClass,
  useCreateClass,
  useDeleteClass,
  useCreateStudent,
  useUpdateStudent,
  useDeleteStudent,
} from "@/api/classes";
import { COURSES } from "@/data/courseSections";

const ClassroomManagement = () => {
  const { courseSlug } = useParams();
  const course = COURSES.find((course) => course.slug === courseSlug);

  const classesQuery = useGetClasses(course?.id || "");
  const createClassMutation = useCreateClass(course?.id || "");
  const deleteClassMutation = useDeleteClass(course?.id || "");

  const [selectedClassroomId, setSelectedClassroomId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (
      classesQuery.isSuccess &&
      classesQuery.data.count > 0 &&
      selectedClassroomId == null
    ) {
      setSelectedClassroomId(classesQuery.data.items[0].id);
    }
  }, [classesQuery]);

  const classQuery = useGetClass(course?.id || "", selectedClassroomId);
  const createStudentMutation = useCreateStudent(
    course?.id || "",
    selectedClassroomId || "",
  );
  const updateStudentMutation = useUpdateStudent(
    course?.id || "",
    selectedClassroomId || "",
  );
  const deleteStudentMutation = useDeleteStudent(
    course?.id || "",
    selectedClassroomId || "",
  );

  const [isNewClassDialogOpen, setIsNewClassDialogOpen] = useState(false);
  const { toast } = useToast();

  const selectedClassroom = classesQuery.data?.items.find(
    (c) => c.id === selectedClassroomId,
  );

  const onCreateClassroom = (data: { name: string }) => {
    createClassMutation.mutate({ name: data.name });
    const newClassroom: Classroom = {
      id: crypto.randomUUID(),
      name: data.name,
      students: [],
    };

    setSelectedClassroomId(newClassroom.id);
    setIsNewClassDialogOpen(false);

    toast({
      title: "Classroom created",
      description: `${data.name} has been created`,
    });
  };

  const onAddStudent = (data: { name: string; email?: string }) => {
    if (!selectedClassroomId) return;

    createStudentMutation.mutate({
      name: data.name,
      email: data.email,
    });

    toast({
      title: "Student added",
      description: `${data.name} has been added to the class`,
    });
  };

  const deleteStudent = (studentId: string) => {
    if (!selectedClassroomId) return;
    deleteStudentMutation.mutate({ studentId });

    toast({
      title: "Student removed",
      description: "The student has been removed from the class",
    });
  };

  const editStudent = (
    studentId: string,
    data: { name: string; email: string },
  ) => {
    if (!selectedClassroomId) return;
    updateStudentMutation.mutate({
      studentId,
      name: data.name,
      email: data.email,
    });

    toast({
      title: "Student updated",
      description: `${data.name}'s information has been updated`,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <CreateClassroomDialog
          isOpen={isNewClassDialogOpen}
          onOpenChange={setIsNewClassDialogOpen}
          onSubmit={onCreateClassroom}
        />
      </div>

      {classesQuery.isSuccess ? (
        <Tabs
          value={selectedClassroomId || undefined}
          onValueChange={setSelectedClassroomId}
          className="w-full"
        >
          <TabsList className="w-full justify-start border-b rounded-none p-0 h-auto bg-transparent">
            {classesQuery.data.items.map((classroom) => {
              return (
                <TabsTrigger
                  key={classroom.id}
                  value={classroom.id}
                  className="px-6 py-3 rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-teachstack-purple"
                >
                  {classroom.name}

                  {selectedClassroomId == classroom.id && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteClassMutation.mutate(classroom.id);
                      }}
                    >
                      <X className="h-4 w-4 ml-2" />
                    </button>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {selectedClassroomId && selectedClassroom ? (
            <TabsContent
              key={selectedClassroom.id}
              value={selectedClassroom.id}
            >
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="border-b border-gray-200">
                    <div className="bg-gray-50 px-4 py-2">
                      <h3 className="font-medium">
                        Add New Student to {selectedClassroom.name}
                      </h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <StudentForm onSubmit={onAddStudent} />
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="border-b border-gray-200">
                    <div className="bg-gray-50 px-4 py-2">
                      <h3 className="font-medium">
                        Students in {selectedClassroom.name}
                      </h3>
                    </div>
                  </div>
                  <div className="p-0">
                    {classQuery.isSuccess && (
                      <StudentsTable
                        students={classQuery.data.items}
                        onDeleteStudent={deleteStudent}
                        onEditStudent={editStudent}
                      />
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>
          ) : null}
        </Tabs>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No classrooms created yet. Create a classroom using the button above.
        </div>
      )}
    </div>
  );
};

export default ClassroomManagement;
